import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApprovalState, ApprovalType } from '@mg/shared';
import type { ApprovalDecisionDto, Campaign, Post } from '@mg/shared';
import { api, ApiRequestError } from '../../lib/api';
import { approvalStateLabel, approvalStateTone, enumLabel, formatDateTime } from '../../lib/display';
import { Badge } from '../../components/ui/Badge';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { useDialogs } from '../../components/ui/DialogProvider';
import { ApprovalActionBar } from '../approval/ApprovalActionBar';
import { DecisionCommentModal, type DecisionCommentTarget } from '../approval/DecisionCommentModal';

/**
 * Composer "Approval" tab. Shows the owning campaign (name + status + link), this post's own
 * status, the per-post approval controls (POST mode — reuses the shared ApprovalActionBar so it
 * matches the campaign page: Submit / Approve / Cancel Review / Request changes / Reject / Clear
 * compliance, with the comment modal), and the approval history for THIS post only.
 *
 * Local state (seeded from the post, updated from each mutation result) so acting here never
 * re-hydrates the composer form (mirrors the old PostApprovalBar); the ['post'] query is
 * intentionally NOT invalidated.
 */
export function PostApprovalPanel({
  post,
  tenantKey,
  approvalType,
  campaign,
  submitHold = null,
}: {
  post: Post;
  tenantKey: string;
  approvalType: ApprovalType;
  campaign: Campaign | null;
  /**
   * Why the post cannot be scheduled or published as the composer's form stands (its gates), or
   * null. Submit is held for the same reason, said under it: approval is what lets a post go out, so
   * one that cannot go out - a social post with no text - is not sent for it.
   */
  submitHold?: string | null;
}) {
  const queryClient = useQueryClient();
  const { alert } = useDialogs();
  const [state, setState] = useState<ApprovalState>(post.approvalState ?? ApprovalState.DRAFT);
  const [pending, setPending] = useState<string[]>(post.pendingApproverNames ?? []);
  const [decisionTarget, setDecisionTarget] = useState<DecisionCommentTarget | null>(null);

  const historyQ = useQuery({
    queryKey: ['post-transitions', tenantKey, post.id],
    queryFn: ({ signal }) => api.getPostTransitions(post.id, signal),
  });

  const applyResult = (updated: Post) => {
    setState(updated.approvalState ?? ApprovalState.DRAFT);
    setPending(updated.pendingApproverNames ?? []);
    // Push the updated post into the ['post'] cache (setQueryData, NOT invalidate) so the composer's
    // Content/Schedule gating (readOnly/releasable) reflects the new state — without a refetch that
    // would re-hydrate and clobber unsaved edits (the composer hydrates once per post id).
    queryClient.setQueryData(['post', tenantKey, updated.id], updated);
    // Refresh list surfaces + this post's history.
    void queryClient.invalidateQueries({ queryKey: ['posts', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['campaigns', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['campaign', tenantKey, post.campaignId] });
    void queryClient.invalidateQueries({ queryKey: ['calendar', tenantKey] });
    void queryClient.invalidateQueries({ queryKey: ['post-transitions', tenantKey, post.id] });
  };
  const onError = (verb: string) => (err: unknown) =>
    void alert({
      title: 'Something went wrong',
      message: err instanceof ApiRequestError ? err.message : `Could not ${verb}.`,
    });

  const submit = useMutation({
    mutationFn: () => api.submitPostForApproval(post.id, {}),
    onSuccess: applyResult,
    onError: onError('submit this post for approval'),
  });
  const decide = useMutation({
    mutationFn: (dto: ApprovalDecisionDto) => api.decidePost(post.id, dto),
    onSuccess: applyResult,
    onError: onError('record the decision'),
  });
  const compliance = useMutation({
    mutationFn: () => api.compliancePostCheck(post.id),
    onSuccess: applyResult,
    onError: onError('clear the compliance check'),
  });
  const cancelReview = useMutation({
    mutationFn: () => api.cancelReviewPost(post.id),
    onSuccess: applyResult,
    onError: onError('withdraw this post from review'),
  });

  const busy =
    submit.isPending || decide.isPending || compliance.isPending || cancelReview.isPending;

  return (
    <div className="space-y-4">
      {/* Owning campaign */}
      <Card>
        <CardBody className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-ink-muted">Campaign</p>
            <Link
              to={`/campaigns/${post.campaignId}`}
              className="focus-ring rounded font-medium text-link hover:underline"
            >
              {campaign?.name ?? post.campaignName ?? 'View campaign'}
            </Link>
          </div>
          {campaign && (
            <Badge tone={approvalStateTone[campaign.approvalState]}>
              {approvalStateLabel[campaign.approvalState]}
            </Badge>
          )}
        </CardBody>
      </Card>

      {/* This post's own status — only meaningful in POST mode (per-post approval). In
          CAMPAIGN/NONE modes the post carries no own approvalState, so don't show a misleading
          "Draft" badge; the campaign status above is the source of truth. */}
      {approvalType === ApprovalType.POST && (
        <div className="flex items-center gap-2">
          <span className="text-sm text-ink-muted">This post:</span>
          <Badge tone={approvalStateTone[state]}>{approvalStateLabel[state]}</Badge>
        </div>
      )}

      {/* Per-post approval controls (POST mode) — shared bar, so it matches the campaign page. */}
      {approvalType === ApprovalType.POST ? (
        <ApprovalActionBar
          state={state}
          busy={busy}
          canReview
          canSubmit
          context="post"
          submitBlockedReason={
            post.scheduledAt == null && !post.publishWhenApproved
              ? 'Set a proposed schedule (Schedule tab), or choose “Post as soon as approved”, before submitting.'
              : submitHold
          }
          pendingApproverNames={pending}
          onSubmit={() => submit.mutate()}
          onApprove={() => decide.mutate({ decision: 'approve' })}
          onCancelReview={() => cancelReview.mutate()}
          onRequestChanges={() =>
            setDecisionTarget({
              title: 'Request changes',
              confirmLabel: 'Request changes',
              onConfirm: (comment) => {
                decide.mutate({ decision: 'request-changes', comment });
                setDecisionTarget(null);
              },
            })
          }
          onReject={() =>
            setDecisionTarget({
              title: 'Reject post',
              confirmLabel: 'Reject',
              danger: true,
              onConfirm: (comment) => {
                decide.mutate({ decision: 'reject', comment });
                setDecisionTarget(null);
              },
            })
          }
          onCompliance={() => compliance.mutate()}
        />
      ) : (
        <div className="rounded-xl border border-line bg-card p-4 text-sm text-ink-label shadow-card">
          {approvalType === ApprovalType.NONE
            ? 'Approvals are turned off for this workspace — posts publish straight from Draft.'
            : 'This workspace approves at the campaign level — submit and approve on the campaign.'}
        </div>
      )}

      {/* Approval history — this post only */}
      <Card>
        <CardHeader title="Approval history" description="Decisions recorded on this post." />
        <CardBody className="space-y-2">
          {historyQ.isLoading ? (
            <p className="text-sm text-ink-faint">Loading history…</p>
          ) : historyQ.isError ? (
            <p className="text-sm text-red-600 dark:text-error-dark">Could not load approval history.</p>
          ) : (historyQ.data ?? []).length === 0 ? (
            <p className="text-sm text-ink-faint">No approval activity on this post yet.</p>
          ) : (
            <ul className="space-y-2">
              {historyQ.data!.map((t) => (
                <li key={t.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <span className="text-ink-faint">{formatDateTime(t.createdAt)}</span>
                  <span className="font-medium text-ink-strong">
                    {t.fromState ? `${enumLabel(t.fromState)} → ` : ''}
                    {enumLabel(t.toState)}
                  </span>
                  {t.actorName && (
                    <span className="text-ink-muted">
                      · by <span className="font-medium text-ink-label">{t.actorName}</span>
                    </span>
                  )}
                  {t.comment && <span className="text-ink-muted">· “{t.comment}”</span>}
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      <DecisionCommentModal target={decisionTarget} onClose={() => setDecisionTarget(null)} />
    </div>
  );
}
